import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { viewerDisabledProps, isAdmin, isViewer } from "../utils/permissions";

const LeaveApprovalPage = () => {
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("pending");
  const userId = localStorage.getItem("user_id");

  useEffect(() => {
    fetchAdjustments();
  }, [filter]);

  const fetchAdjustments = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `https://ims-attendanceback.indevconsultancy.in/api/leave-adjustment/?approval_status=${filter}`
      );
      if (res.data.status) {
        setAdjustments(res.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching adjustments:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (adjustmentId) => {
    const result = await Swal.fire({
      title: "Approve Leave Request?",
      text: "This will deduct credits from employee's balance.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Approve",
    });

    if (!result.isConfirmed) return;

    try {
      const res = await axios.put(
        "https://ims-attendanceback.indevconsultancy.in/api/leave-adjustment/",
        {
          adjustment_id: adjustmentId,
          approval_status: "approved",
          approved_by: userId,
        }
      );

      if (res.data.status) {
        Swal.fire("Approved!", res.data.message, "success");
        fetchAdjustments();
      }
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Failed to approve", "error");
    }
  };

  const handleReject = async (adjustmentId) => {
    const result = await Swal.fire({
      title: "Reject Leave Request?",
      text: "Credits will not be deducted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Reject",
    });

    if (!result.isConfirmed) return;

    try {
      const res = await axios.put(
        "https://ims-attendanceback.indevconsultancy.in/api/leave-adjustment/",
        {
          adjustment_id: adjustmentId,
          approval_status: "rejected",
          approved_by: userId,
        }
      );

      if (res.data.status) {
        Swal.fire("Rejected!", res.data.message, "success");
        fetchAdjustments();
      }
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Failed to reject", "error");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB");
  };

  // The "to" date is derived: adjustment_date + (credits_used - 1) days.
  const computeToDate = (fromDateStr, days) => {
    if (!fromDateStr) return "-";
    const d = parseFloat(days || 1);
    const dt = new Date(fromDateStr);
    dt.setDate(dt.getDate() + (d > 0 ? d - 1 : 0));
    return dt.toLocaleDateString("en-GB");
  };

  // Admins and viewers can approve / reject. Kitchen role still cannot.
  const canApprove = isAdmin() || isViewer();
  const showActionsCol = filter === "pending" && canApprove;

  return (
    <div className="px-3 py-3 bg-cream" style={{ minHeight: "100vh" }}>
      <div className="card">
        <div className="card-header bg-skyBlue text-white d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Leave Approval Management</h5>
          <div>
            <select
              className="form-select form-select-sm"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={{ width: "auto", display: "inline-block" }}
            >
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status"></div>
              <p>Loading requests...</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered table-striped">
                <thead className="table-light">
                  <tr>
                    <th>S.No</th>
                    <th>From Date</th>
                    <th>To Date</th>
                    <th>Employee</th>
                    <th>Kitchen</th>
                    <th>Leave Type</th>
                    <th>Leave Days</th>
                    <th>Credits Used</th>
                    <th>Reason</th>
                    <th>Status</th>
                    {showActionsCol && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {adjustments.length === 0 ? (
                    <tr>
                      <td colSpan={showActionsCol ? "11" : "10"} className="text-center">
                        No {filter} requests found
                      </td>
                    </tr>
                  ) : (
                    adjustments.map((adj, index) => (
                      <tr key={adj.id}>
                        <td>{index + 1}</td>
                        <td>{formatDate(adj.adjustment_date)}</td>
                        <td>{computeToDate(adj.adjustment_date, adj.credits_used)}</td>
                        <td>
                          {adj.employee_name}
                          <br />
                          <small className="text-muted">{adj.employee_code}</small>
                        </td>
                        <td>{adj.kitchen_name}</td>
                        <td>
                          <span className="badge bg-info text-dark">
                            {adj.leave_type_display || adj.leave_type || "—"}
                          </span>
                        </td>
                        <td>{adj.leave_days_granted}</td>
                        <td>
                          <span className="badge bg-warning">{adj.credits_used}</span>
                        </td>
                        <td>{adj.reason || "-"}</td>
                        <td>
                          {adj.approval_status === "pending" && (
                            <span className="badge bg-warning">Pending</span>
                          )}
                          {adj.approval_status === "approved" && (
                            <span className="badge bg-success">Approved</span>
                          )}
                          {adj.approval_status === "rejected" && (
                            <span className="badge bg-danger">Rejected</span>
                          )}
                        </td>
                        {filter === "pending" && (
                          <td>
                            <button
                              className="btn btn-sm btn-success me-1"
                              onClick={() => handleApprove(adj.id)}
                            >
                              Approve
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleReject(adj.id)}
                            >
                              Reject
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaveApprovalPage;